import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton } from "common-fe";
import useReviewQueue from "../hooks/useReviewQueue";
import useFvdPushFailures from "../hooks/useFvdPushFailures";
import AttachRideDialog from "../components/AttachRideDialog";
import RideLink from "../components/RideLink";
import ClassLink from "../components/ClassLink";
import FvdPushFailureCauseChip from "../components/FvdPushFailureCauseChip";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { classNumberLabel, formatVenueLocalDateTime } from "../util/classDisplay";
import { parseFvdPushError } from "../util/fvdPushError";

// #387 — admin half of the emergency-ride hatch (#386/#388): a review queue
// of manual rides still flagged needsReview, with a per-row "attach to real
// ride" action. A ride stays in this queue until explicitly resolved — the
// list is server-filtered by needsReview=true, so a resolved ride just drops
// out on refetch (marked resolved server-side, never deleted).
export default function ReviewQueue() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.reviewQueue"));
  const { rides, loading, error, refetch } = useReviewQueue();
  const {
    rides: fvdFailureRides,
    loading: fvdFailuresLoading,
    error: fvdFailuresError,
  } = useFvdPushFailures();
  const [selectedRide, setSelectedRide] = useState(null);
  const [search, setSearch] = useState("");

  // #869 — search over CNO/rider/horse/class, matching the ClassManager /
  // PeopleManager / HorseManager filtered-list pattern. Class text prefers
  // the resolved classNumberLabel (#864's join) so a search matches the
  // visible label, falling back to the raw classId when the lookup missed.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rides;
    return rides.filter((ride) =>
      [ride.competitorNumber, ride.riderName, ride.horseName, ride.class ? classNumberLabel(ride.class) : ride.classId].some(
        (v) => v != null && String(v).toLowerCase().includes(q),
      ),
    );
  }, [rides, search]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("reviewQueue.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.subtitle")}
      </Typography>

      <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
        {t("reviewQueue.manualSection.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("reviewQueue.loadError")}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton rows={4} columns={3} />
      ) : rides.length === 0 ? (
        <Typography color="text.secondary">{t("reviewQueue.empty")}</Typography>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            sx={{ mb: 2, maxWidth: 360 }}
            placeholder={t("reviewQueue.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {filtered.length === 0 ? (
            <Typography color="text.secondary">{t("reviewQueue.noResults")}</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reviewQueue.columns.ride")}</TableCell>
                    <TableCell>{t("reviewQueue.columns.class")}</TableCell>
                    <TableCell align="right">{t("reviewQueue.columns.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((ride) => {
                    const id = ride.id ?? ride.rideId;
                    // #865 — surface a failed lookup as a placeholder, not the
                    // raw id; the id still reaches support via the tooltip.
                    const classLookupMissed = !!ride.classId && !ride.class;
                    return (
                      <TableRow key={id} hover>
                        <TableCell>
                          {/* #860 — shared RideLink ("#CNO, Rider on Horse"),
                              linked into the RideEditor route. Supersedes the
                              separate CNO/horse/rider columns; this row was
                              previously not linked at all. */}
                          <RideLink ride={ride} rideId={id} />
                        </TableCell>
                        <TableCell>
                          {/* #864 — shared ClassLink; ride.class is the resolved
                              class object from useReviewQueue's best-effort lookup
                              (null on a failed lookup — link falls back to the
                              placeholder below). */}
                          <Tooltip title={classLookupMissed ? ride.classId : ""}>
                            <span>
                              <ClassLink
                                classObj={ride.class}
                                classId={ride.classId}
                                fallback={t("common.unknownClass")}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: "text.primary", borderColor: "text.primary" }}
                            onClick={() => setSelectedRide(ride)}
                          >
                            {t("reviewQueue.attachAction")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Card #1608 — its OWN section, deliberately not merged into the
          manual-review table above: the operator action differs (unlock/retry
          an FVD write vs. finish/verify a manual entry), and the columns
          differ (cause/attempts/raw error vs. none of that). Driver decision
          recorded on the card. */}
      <Typography variant="h6" color="text.primary" sx={{ mt: 4, mb: 1 }}>
        {t("reviewQueue.fvdFailures.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.fvdFailures.subtitle")}
      </Typography>

      {fvdFailuresError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fvdFailuresError.message || t("reviewQueue.fvdFailures.loadError")}
        </Alert>
      )}

      {fvdFailuresLoading ? (
        <TableSkeleton rows={4} columns={5} />
      ) : fvdFailureRides.length === 0 ? (
        <Typography color="text.secondary">{t("reviewQueue.fvdFailures.empty")}</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("reviewQueue.fvdFailures.columns.ride")}</TableCell>
                <TableCell>{t("reviewQueue.fvdFailures.columns.class")}</TableCell>
                <TableCell>{t("reviewQueue.fvdFailures.columns.cause")}</TableCell>
                <TableCell align="right">{t("reviewQueue.fvdFailures.columns.attempts")}</TableCell>
                <TableCell>{t("reviewQueue.fvdFailures.columns.detail")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fvdFailureRides.map((ride) => {
                const id = ride.id ?? ride.rideId;
                const classLookupMissed = !!ride.classId && !ride.class;
                const { cause, attempts, rawError } = parseFvdPushError(ride.fvdPushError);
                const pushedAt = ride.fvdPushedAt ? formatVenueLocalDateTime(ride.fvdPushedAt) : null;
                return (
                  <TableRow key={id} hover data-testid="fvd-push-failure-row">
                    <TableCell>
                      <RideLink ride={ride} rideId={id} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={classLookupMissed ? ride.classId : ""}>
                        <span>
                          <ClassLink
                            classObj={ride.class}
                            classId={ride.classId}
                            fallback={t("common.unknownClass")}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={pushedAt ? t("reviewQueue.fvdFailures.pushedAtTooltip", { when: pushedAt }) : ""}>
                        <span>
                          <FvdPushFailureCauseChip cause={cause} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" data-testid="fvd-push-failure-attempts">
                      {attempts ?? t("reviewQueue.fvdFailures.attemptsUnknown")}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={rawError || ""}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid="fvd-push-failure-detail"
                          sx={{
                            maxWidth: 360,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {rawError || t("reviewQueue.fvdFailures.noDetail")}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AttachRideDialog
        open={!!selectedRide}
        manualRide={selectedRide}
        onClose={() => setSelectedRide(null)}
        onResolved={refetch}
      />
    </Box>
  );
}
